import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { UserPlus, Loader2, Trash2, AlertTriangle, ShieldCheck, Lock } from "lucide-react";

const ROLES = ["trainer", "employee", "manager", "hr", "admin"];

export default function UsersPage() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmStep, setConfirmStep] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "trainer", username: "", password: "", department: "", position: "" });

  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const departments = settings?.departments || [];
  const deptPositions = departments.find((d) => d.name === form.department)?.positions || [];

  const { data: candidates = [], isLoading: candidatesLoading } = useQuery({
    queryKey: ["cleanup-candidates"],
    queryFn: async () => (await api.get("/admin/cleanup-candidates")).data,
    enabled: cleanupOpen,
  });

  const openCleanup = () => { setSelectedIds(new Set()); setConfirmStep(false); setPreview(null); setCleanupOpen(true); };
  const toggleSelect = (uid) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(uid) ? next.delete(uid) : next.add(uid);
    return next;
  });

  const previewMutation = useMutation({
    mutationFn: async () => (await api.post("/admin/cleanup-preview", { user_ids: [...selectedIds] })).data,
    onSuccess: (d) => { setPreview(d); setConfirmStep(true); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not build preview"),
  });

  const cleanupMutation = useMutation({
    mutationFn: async () => (await api.post("/admin/cleanup-test-data", { user_ids: [...selectedIds] })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["cleanup-candidates"] });
      toast.success(`Removed ${d.deleted_users} account(s); deleted ${d.deleted_sessions} unprotected session(s)`);
      setCleanupOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Cleanup failed"),
  });

  const createMutation = useMutation({
    mutationFn: async () => api.post("/users", form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User added"); setAddOpen(false); setForm({ email: "", name: "", role: "trainer", username: "", password: "", department: "", position: "" }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add user"),
  });
  const updateMutation = useMutation({
    mutationFn: async ({ id, patch }) => api.patch(`/users/${id}`, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User updated"); },
  });

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Users</h1>
          <p className="text-muted-foreground mt-1">Manage staff access and roles.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2 h-11 rounded-xl text-destructive border-destructive/30 hover:bg-destructive/10" onClick={openCleanup} data-testid="cleanup-data-button"><Trash2 size={18} /> Clean up test data</Button>
          <Button className="gap-2 h-11 rounded-xl" onClick={() => setAddOpen(true)} data-testid="add-user-button"><UserPlus size={18} /> Add User</Button>
        </div>
      </div>

      <Card className="overflow-x-auto">
        {isLoading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden md:table-cell">Joined</TableHead>
                <TableHead>Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => updateMutation.mutate({ id: u.user_id, patch: { role: v } })}>
                      <SelectTrigger className="w-[130px] h-8 capitalize" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs">{fmtDate(u.created_at)}</TableCell>
                  <TableCell>
                    <Switch checked={u.is_active !== false} onCheckedChange={(v) => updateMutation.mutate({ id: u.user_id, patch: { is_active: v } })} data-testid={`active-toggle-${u.user_id}`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add User</DialogTitle>
            <DialogDescription>Create a staff account. Trainers &amp; Trainees use a username &amp; password; Admins &amp; Managers use their company Google email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="capitalize" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r === "employee" ? "trainee" : r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {["trainer", "employee"].includes(form.role) ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="e.g. jsmith" data-testid="user-username-input" /></div>
                  <div className="space-y-2"><Label>Initial Password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="set a password" data-testid="user-password-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v, position: "" })}>
                      <SelectTrigger data-testid="user-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                      <SelectContent>{departments.map((d) => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Position</Label>
                    <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })} disabled={!form.department}>
                      <SelectTrigger data-testid="user-position-select"><SelectValue placeholder={form.department ? "Select position" : "Pick a department"} /></SelectTrigger>
                      <SelectContent>{deptPositions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Trainers &amp; Trainees sign in with this username &amp; password. Their position controls which library materials they see.</p>
              </>
            ) : (
              <>
                <div className="space-y-2"><Label>Email (Google account)</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
                <p className="text-xs text-muted-foreground">Admins &amp; Managers sign in with Google using this company email.</p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button disabled={!form.name || (["trainer","employee"].includes(form.role) ? (!form.username || !form.password) : !form.email) || createMutation.isPending} onClick={() => createMutation.mutate()} className="gap-2" data-testid="submit-user-button">
              {createMutation.isPending && <Loader2 size={16} className="animate-spin" />} Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
        <DialogContent data-testid="cleanup-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle size={18} className="text-destructive" /> Clean up test data</DialogTitle></DialogHeader>

          {!confirmStep ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Tick only the <b>test/fake accounts</b> you want to remove. Real staff are locked and cannot be selected.
                Trainings that are completed/signed, or that were run by a real trainer, manager, or admin, are always kept.
              </p>
              {candidatesLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="animate-spin text-muted-foreground" /></div>
              ) : (
                <ScrollArea className="h-[340px] rounded-lg border" data-testid="cleanup-candidate-list">
                  <div className="divide-y">
                    {candidates.map((c) => (
                      <label
                        key={c.user_id}
                        className={`flex items-center gap-3 px-3 py-2.5 text-sm ${c.locked ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:bg-muted/50"}`}
                        data-testid={`cleanup-candidate-${c.user_id}`}
                      >
                        <Checkbox
                          checked={selectedIds.has(c.user_id)}
                          disabled={c.locked}
                          onCheckedChange={() => !c.locked && toggleSelect(c.user_id)}
                          data-testid={`cleanup-checkbox-${c.user_id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{c.name || c.username || c.email || "Unnamed"}</div>
                          <div className="text-xs text-muted-foreground truncate font-mono">{c.email || c.username}</div>
                        </div>
                        <Badge variant="outline" className="capitalize shrink-0">{c.role === "employee" ? "trainee" : c.role}</Badge>
                        {c.locked ? (
                          <Badge className="gap-1 bg-emerald-600/10 text-emerald-700 border-emerald-600/20 shrink-0"><Lock size={11} /> Staff</Badge>
                        ) : c.sessions_protected > 0 ? (
                          <Badge variant="outline" className="gap-1 text-emerald-700 border-emerald-600/30 shrink-0" title="Has protected trainings that will be kept"><ShieldCheck size={11} /> {c.sessions_protected} kept</Badge>
                        ) : null}
                      </label>
                    ))}
                  </div>
                </ScrollArea>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setCleanupOpen(false)}>Cancel</Button>
                <Button variant="destructive" disabled={selectedIds.size === 0 || previewMutation.isPending} onClick={() => previewMutation.mutate()} className="gap-2" data-testid="cleanup-continue-button">
                  {previewMutation.isPending && <Loader2 size={16} className="animate-spin" />} Review {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Review before removing. This cannot be undone, but no real, signed, or staff-conducted trainings are affected.
              </p>
              <div className="rounded-lg border bg-muted/40 p-4 text-sm space-y-1" data-testid="cleanup-preview">
                <div className="flex justify-between"><span className="text-destructive">Accounts to remove</span><span className="font-mono font-medium text-destructive">{preview?.accounts_to_delete}</span></div>
                <div className="flex justify-between"><span className="text-destructive">Unprotected sessions to delete</span><span className="font-mono font-medium text-destructive">{preview?.sessions_to_delete}</span></div>
                <div className="flex justify-between"><span className="text-emerald-700">Protected trainings kept</span><span className="font-mono font-medium text-emerald-700">{preview?.sessions_protected}</span></div>
                <div className="flex justify-between"><span className="text-emerald-700">Certificates kept</span><span className="font-mono font-medium text-emerald-700">{preview?.certificates_protected}</span></div>
              </div>
              <div className="rounded-lg border border-emerald-600/20 bg-emerald-600/5 p-3 text-xs text-emerald-800 flex gap-2">
                <ShieldCheck size={15} className="shrink-0 mt-0.5" />
                <span>Trainings conducted or signed by a real trainer, manager, or admin (e.g. Barton, Claudia) and any completed/signed training are preserved as permanent records. SOP library documents are never affected.</span>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirmStep(false)} data-testid="cleanup-back-button">Back</Button>
                <Button variant="destructive" disabled={cleanupMutation.isPending} onClick={() => cleanupMutation.mutate()} className="gap-2" data-testid="confirm-cleanup-button">
                  {cleanupMutation.isPending && <Loader2 size={16} className="animate-spin" />} Remove {preview?.accounts_to_delete} account(s)
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
